<script lang="ts" setup>
import { goToRoute } from "~/common/helpers"
import { useDefaultSettingsStore } from "~/stores/defaultSettingsStore"

/**
 *
 *  Props and emits section
 *
 *
 */

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const defaultSettingsStore = useDefaultSettingsStore()
const { settings } = storeToRefs(defaultSettingsStore)

const information = computed(() => {
    return settings.value?.information
})
const contacts = computed(() => {
    return settings.value?.contacts
})
const label_schedule_footer = computed(() => {
    return settings.value?.contacts?.schedule
})

const { isPartnerMode } = usePartnerBranding()

const scheduleLabels = computed(() => {
    const dayWeek = [
        "Lunes",
        "Martes",
        "Miercoles",
        "Jueves",
        "Viernes",
        "Sábado",
        "Domingo",
        "Lunes",
    ]
    const labels = ref<string[]>([])
    if (
        label_schedule_footer &&
        label_schedule_footer.value &&
        label_schedule_footer.value.length > 0
    ) {
        label_schedule_footer.value.forEach(
            (schedule: { day_week: any[]; hours: string | any[] }) => {
                let label = ""

                schedule.day_week.forEach((week) => {
                    if (Array.isArray(week) && week.length === 2) {
                        label +=
                            (label ? ", " : "") +
                            dayWeek[week[0]] +
                            " a " +
                            dayWeek[week[1]]
                    } else {
                        label += (label ? ", " : "") + dayWeek[week]
                    }
                })

                if (schedule.hours.length === 1) {
                    label +=
                        ": " +
                        schedule.hours[0].timeInit +
                        " - " +
                        schedule.hours[0].timeEnd
                } else if (schedule.hours.length === 2) {
                    label +=
                        ": " +
                        schedule.hours[0].timeInit +
                        " - " +
                        schedule.hours[0].timeEnd +
                        " y " +
                        schedule.hours[1].timeInit +
                        " - " +
                        schedule.hours[1].timeEnd
                }

                labels.value.push(label)
            }
        )
    } else {
        labels.value = []
    }

    return labels.value
})

const hasAnyInformation = computed(() => {
    return (
        information.value?.enable_term_conditions ||
        information.value?.enable_warranty_policy ||
        information.value?.enable_shipment_policy ||
        information.value?.enable_return_policy ||
        information.value?.enable_frequent_asks
    )
})

/**
 *
 *  Life cicle events
 *
 *
 */

/**
 *
 *  Functions section
 *
 *
 */

function openNetwork(url: string) {
    window.open(url)
}
</script>
<template>
    <div>
        <div class="footer tw-py-8 max-container tw-max-w-full">
            <!-- Desktop layout (md and above) -->
            <div
                class="tw-hidden md:tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-8"
            >
                <!-- Logo + Horario de atención -->
                <div class="tw-col-span-3 tw-flex tw-flex-col tw-gap-6">
                    <ApplicationLogo
                        :class="{ 'tw-w-56': !isPartnerMode }"
                        src="/img/logo-white-footer.svg"
                        partnerAltLogo="true"
                        @click="goToRoute('/')"
                    />
                    <div
                        v-if="scheduleLabels.length > 0"
                        class="footer-list !tw-p-0"
                    >
                        <p>Horario de atención</p>
                        <ul
                            class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                        >
                            <li
                                v-for="day in scheduleLabels"
                                :key="day"
                                class="!tw-flex-col !tw-items-start !tw-gap-0"
                            >
                                <span
                                    class="tw-font-semibold tw-text-white tw-text-sm"
                                    >{{ day.split(":")[0] }}:</span
                                >
                                <span class="tw-text-sm">{{
                                    day.split(":").slice(1).join(":")
                                }}</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- Contacto comercial -->
                <div class="footer-list tw-col-span-2">
                    <p>Contacto comercial</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li v-if="contacts?.team_name">
                            <q-icon name="info" color="white" />
                            <span class="tw-text-sm">{{
                                contacts.team_name
                            }}</span>
                        </li>
                        <li v-if="contacts?.address">
                            <q-icon
                                name="fa-solid fa-location-dot"
                                color="white"
                            />
                            <span
                                class="tw-text-sm tw-cursor-pointer"
                                @click="
                                    openNetwork(
                                        'http://www.google.com/maps/place/' +
                                            contacts?.lat +
                                            ',' +
                                            contacts?.long
                                    )
                                "
                            >
                                {{ contacts.address }}
                            </span>
                        </li>
                        <li v-if="contacts?.phones">
                            <q-icon name="fa-solid fa-phone" color="white" />
                            <template v-if="Array.isArray(contacts.phones)">
                                <a
                                    v-for="phone in contacts.phones"
                                    :key="phone"
                                    :href="`tel:${phone}`"
                                    class="tw-text-sm"
                                    >{{ phone }}</a
                                >
                            </template>
                            <a
                                v-else
                                :href="`tel:${contacts.phones}`"
                                class="tw-text-sm"
                                >{{ contacts.phones }}</a
                            >
                        </li>
                        <li v-if="contacts?.email">
                            <q-icon name="fa-solid fa-envelope" color="white" />
                            <a
                                :href="`mailto:${contacts.email}`"
                                class="tw-text-sm"
                                >{{ contacts.email }}</a
                            >
                        </li>
                    </ul>
                </div>

                <!-- Información (desktop) -->
                <div v-if="hasAnyInformation" class="footer-list tw-col-span-2">
                    <p>{{ $t("settings.information") }}</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li v-if="information?.enable_about_us">
                            <NuxtLink to="/info"
                                ><span class="tw-text-sm">{{
                                    $t("settings.about_us")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_term_conditions">
                            <NuxtLink to="/information/terms-conditions"
                                ><span class="tw-text-sm">{{
                                    $t("settings.terms_conditions")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_warranty_policy">
                            <NuxtLink to="/information/seg-priv-policy"
                                ><span class="tw-text-sm">{{
                                    $t("settings.seg_and_privacy_policy")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_shipment_policy">
                            <NuxtLink to="/information/shipment-policy"
                                ><span class="tw-text-sm">{{
                                    $t("settings.shipment_policy")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_return_policy">
                            <NuxtLink to="/information/return-policy"
                                ><span class="tw-text-sm">{{
                                    $t("settings.return_policy")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_frequent_asks">
                            <NuxtLink to="/information/frequent-asks"
                                ><span class="tw-text-sm">{{
                                    $t("settings.frequent_asks")
                                }}</span></NuxtLink
                            >
                        </li>
                    </ul>
                </div>

                <div class="footer-list tw-col-span-2">
                    <p>Compras online para Cuba desde:</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li>
                            <NuxtLink to="/comprar-en-cuba-online"
                                ><span class="tw-text-sm">Cuba</span></NuxtLink
                            >
                        </li>
                        <li>
                            <NuxtLink to="/comprar-en-cuba-desde-espana"
                                ><span class="tw-text-sm"
                                    >España</span
                                ></NuxtLink
                            >
                        </li>
                        <li>
                            <NuxtLink to="/comprar-en-cuba-desde-miami"
                                ><span class="tw-text-sm">Miami</span></NuxtLink
                            >
                        </li>
                        <li>
                            <NuxtLink to="/comprar-en-cuba-desde-otros-paises"
                                ><span class="tw-text-sm"
                                    >Otros países</span
                                ></NuxtLink
                            >
                        </li>
                    </ul>
                </div>

                <!-- Enlaces -->
                <div class="footer-list tw-col-span-2">
                    <p>Enlaces</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li>
                            <NuxtLink to="/profile"
                                ><span class="tw-text-sm"
                                    >Mi cuenta</span
                                ></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_about_us">
                            <NuxtLink to="/info"
                                ><span class="tw-text-sm"
                                    >Quiénes somos</span
                                ></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_frequent_asks">
                            <NuxtLink to="/information/frequent-asks"
                                ><span class="tw-text-sm">FAQ</span></NuxtLink
                            >
                        </li>
                        <li>
                            <NuxtLink to="/contact"
                                ><span class="tw-text-sm"
                                    >Contacto</span
                                ></NuxtLink
                            >
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Mobile layout (below md) -->
            <div
                class="md:tw-hidden tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-8"
            >
                <!-- Logo -->
                <div class="tw-w-full tw-col-span-12 tw-text-center">
                    <ApplicationLogo
                        class="tw-mx-auto tw-w-[170px] tw-h-[70px] md:tw-w-56"
                        src="/img/logo-white-footer.svg"
                        @click="goToRoute('/')"
                    />
                </div>

                <!-- Horario de atención -->
                <div
                    v-if="scheduleLabels.length > 0"
                    class="footer-list tw-col-span-6 tw-text-left"
                >
                    <p>Horario de atención</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li
                            v-for="day in scheduleLabels"
                            :key="day"
                            class="!tw-flex-col !tw-items-start !tw-gap-0"
                        >
                            <span
                                class="tw-font-semibold tw-text-white tw-text-sm"
                                >{{ day.split(":")[0] }}:</span
                            >
                            <span class="tw-text-sm">{{
                                day.split(":").slice(1).join(":")
                            }}</span>
                        </li>
                    </ul>
                </div>

                <!-- Contáctenos -->
                <div class="footer-list tw-col-span-6 tw-text-left">
                    <p>Contáctenos</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li v-if="contacts?.address">
                            <q-icon
                                name="fa-solid fa-location-dot"
                                color="white"
                            />
                            <span
                                class="tw-text-sm tw-cursor-pointer tw-break-all"
                                @click="
                                    openNetwork(
                                        'http://www.google.com/maps/place/' +
                                            contacts?.lat +
                                            ',' +
                                            contacts?.long
                                    )
                                "
                            >
                                {{ contacts?.address }}
                            </span>
                        </li>
                        <li v-if="contacts?.phones">
                            <q-icon name="fa-solid fa-phone" color="white" />
                            <template v-if="Array.isArray(contacts?.phones)">
                                <a
                                    v-for="phone in contacts.phones"
                                    :key="phone"
                                    :href="`tel:${phone}`"
                                    class="tw-text-sm tw-break-all"
                                    >{{ phone }}</a
                                >
                            </template>
                            <a
                                v-else
                                :href="`tel:${contacts.phones}`"
                                class="tw-text-sm tw-break-all"
                                >{{ contacts.phones }}</a
                            >
                        </li>
                        <li v-if="contacts?.email">
                            <q-icon name="fa-solid fa-envelope" color="white" />
                            <a
                                :href="`mailto:${contacts.email}`"
                                class="tw-text-sm tw-break-all"
                                >{{ contacts.email }}</a
                            >
                        </li>
                    </ul>
                </div>

                <!-- Información -->
                <div
                    v-if="hasAnyInformation"
                    class="footer-list tw-col-span-6 tw-text-left"
                >
                    <p>{{ $t("settings.information") }}</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li v-if="information?.enable_about_us">
                            <NuxtLink to="/info"
                                ><span class="tw-text-sm">{{
                                    $t("settings.about_us")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_term_conditions">
                            <NuxtLink to="/information/terms-conditions"
                                ><span class="tw-text-sm">{{
                                    $t("settings.terms_conditions")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_warranty_policy">
                            <NuxtLink to="/information/seg-priv-policy"
                                ><span class="tw-text-sm">{{
                                    $t("settings.seg_and_privacy_policy")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_shipment_policy">
                            <NuxtLink to="/information/shipment-policy"
                                ><span class="tw-text-sm">{{
                                    $t("settings.shipment_policy")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_return_policy">
                            <NuxtLink to="/information/return-policy"
                                ><span class="tw-text-sm">{{
                                    $t("settings.return_policy")
                                }}</span></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_frequent_asks">
                            <NuxtLink to="/information/frequent-asks"
                                ><span class="tw-text-sm">{{
                                    $t("settings.frequent_asks")
                                }}</span></NuxtLink
                            >
                        </li>
                    </ul>
                </div>

                <!-- Compras online para Cuba -->
                <div class="footer-list tw-col-span-6 tw-text-left">
                    <p>Compras online para Cuba desde:</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li>
                            <NuxtLink to="/comprar-en-cuba-online"
                                ><span class="tw-text-sm">Cuba</span></NuxtLink
                            >
                        </li>
                        <li>
                            <NuxtLink to="/comprar-en-cuba-desde-espana"
                                ><span class="tw-text-sm"
                                    >España</span
                                ></NuxtLink
                            >
                        </li>
                        <li>
                            <NuxtLink to="/comprar-en-cuba-desde-miami"
                                ><span class="tw-text-sm">Miami</span></NuxtLink
                            >
                        </li>
                        <li>
                            <NuxtLink to="/comprar-en-cuba-desde-otros-paises"
                                ><span class="tw-text-sm"
                                    >Otros países</span
                                ></NuxtLink
                            >
                        </li>
                    </ul>
                </div>

                <!-- Enlaces -->
                <div class="footer-list tw-col-span-6 tw-text-left">
                    <p>Enlaces</p>
                    <ul
                        class="tw-p-0 tw-list-none tw-flex tw-flex-col tw-items-start tw-gap-3 tw-text-customGray-300"
                    >
                        <li>
                            <NuxtLink to="/profile"
                                ><span class="tw-text-sm"
                                    >Mi cuenta</span
                                ></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_about_us">
                            <NuxtLink to="/info"
                                ><span class="tw-text-sm"
                                    >Quiénes somos</span
                                ></NuxtLink
                            >
                        </li>
                        <li v-if="information?.enable_frequent_asks">
                            <NuxtLink to="/information/frequent-asks"
                                ><span class="tw-text-sm">FAQ</span></NuxtLink
                            >
                        </li>
                        <li>
                            <NuxtLink to="/contact"
                                ><span class="tw-text-sm"
                                    >Contacto</span
                                ></NuxtLink
                            >
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="tw-w-full tw-h-[1px] tw-bg-primary-300"></div>

        <div class="footer tw-py-6 max-container">
            <div
                class="tw-flex tw-justify-between tw-flex-col-reverse tw-gap-7 sm:tw-flex-row tw-items-center tw-mx-auto"
            >
                <div
                    class="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-gap-3 sm:tw-gap-5"
                >
                    <p
                        class="tw-text-white tw-text-xs tw-font-medium tw-leading-none tw-m-0"
                    >
                        {{ $t("landing.copyright") }}
                    </p>
                    <NuxtLink
                        to="/information/cookie-policy"
                        class="tw-text-white tw-text-xs hover:tw-text-secondary-200 hover:tw-underline"
                    >
                        {{ $t("consent.policy_link_short") }}
                    </NuxtLink>
                    <ConsentReopenLink class="tw-text-white" />
                </div>
                <ul
                    class="tw-list-none tw-p-0 tw-flex tw-flex-wrap tw-justify-center tw-gap-4"
                >
                    <li>
                        <img
                            src="../public/img/american-express.svg"
                            alt=""
                            class="images_log tw-w-16 tw-h-8 sm:tw-w-10 sm:tw-h-5"
                        />
                    </li>
                    <li>
                        <img
                            src="../public/img/Visa-logos1.png"
                            alt=""
                            class="images_log tw-w-16 tw-h-8 sm:tw-w-10 sm:tw-h-5"
                        />
                    </li>
                    <li>
                        <img
                            src="../public/img/masterCard.png"
                            alt=""
                            class="images_log tw-w-16 tw-h-8 sm:tw-w-10 sm:tw-h-5"
                        />
                    </li>
                    <li>
                        <img
                            src="../public/img/discover.png"
                            alt=""
                            class="images_log tw-w-16 tw-h-8 sm:tw-w-10 sm:tw-h-5"
                        />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
<style scope>
.footer {
    @apply tw-bg-primary tw-text-white;
}

.footer-list {
    @apply tw-p-2 tw-text-left tw-flex tw-flex-col tw-gap-4;
}

.footer-list p {
    @apply tw-text-base tw-font-bold tw-text-white;
}

.footer-list li {
    @apply tw-text-sm tw-cursor-pointer hover:tw-text-white tw-flex tw-gap-2 tw-items-center;
}

.footer-list li:hover a {
    @apply tw-text-secondary-200 tw-underline;
}

.images_log {
    @apply tw-object-contain;
}
</style>
